import { ReactNode, ComponentType } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Alert,
  Button,
  Container,
  Stack,
  Text,
  Title,
  Code,
  Paper,
  ScrollArea,
} from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

const ErrorFallback = ({ error, resetErrorBoundary }: ErrorFallbackProps) => {
  const isDevelopment = import.meta.env.DEV;

  return (
    <Container size="sm" py={50}>
      <Paper shadow="md" p="xl" radius="md" withBorder>
        <Stack gap="md">
          <Alert
            icon={<IconAlertTriangle size={24} />}
            title="エラーが発生しました"
            color="red"
            variant="filled"
          >
            <Text size="sm" c="white">
              予期しないエラーが発生しました。問題が続く場合は、管理者にお問い合わせください。
            </Text>
          </Alert>

          {isDevelopment && (
            <Paper p="md" radius="sm" bg="gray.0">
              <Stack gap="xs">
                <Title order={6}>エラー詳細（開発環境のみ）</Title>
                <Code block color="red">
                  {error.message}
                </Code>
                {error.stack && (
                  <ScrollArea h={200}>
                    <Code block style={{ fontSize: '0.75rem' }}>
                      {error.stack}
                    </Code>
                  </ScrollArea>
                )}
              </Stack>
            </Paper>
          )}

          <Button
            leftSection={<IconRefresh size={18} />}
            onClick={resetErrorBoundary}
            variant="light"
          >
            再試行
          </Button>
        </Stack>
      </Paper>
    </Container>
  );
};

interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  fallback?: ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: { componentStack: string | null | undefined }) => void;
  onReset?: () => void;
}

export const ErrorBoundaryWrapper = ({
  children,
  fallback = ErrorFallback,
  onError,
  onReset,
}: ErrorBoundaryWrapperProps) => {
  const isDevelopment = import.meta.env.DEV;

  const handleError = (error: Error, errorInfo: { componentStack: string | null | undefined }) => {
    // 開発環境でのコンソールログ
    if (isDevelopment) {
      console.group('🔴 Error Boundary Caught:');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }

    // カスタムエラーハンドラーの呼び出し
    onError?.(error, errorInfo);

    // 本番環境でのエラー報告（将来的にSentryなどを統合）
    if (!isDevelopment) {
      // TODO: Send error to error reporting service
      // logErrorToService(error, errorInfo);
    }
  };

  const handleReset = () => {
    if (isDevelopment) {
      console.log('🔄 Error Boundary Reset');
    }
    onReset?.();
  };

  return (
    <ErrorBoundary FallbackComponent={fallback} onError={handleError} onReset={handleReset}>
      {children}
    </ErrorBoundary>
  );
};